import { useEffect, useState, useCallback, useMemo } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import {
  getOutboundTaskTypes,
  getOutgoingStatistics,
  OutgoingStatisticsRequest,
  selectOutboundTaskTypeOptions,
} from '../../../store/reducers/OutgoingStatisticsSlice';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import { getClinics, selectClinics } from '../../../store/reducers/ClinicSlice';
import { formatDate } from '../../../utils';
import { useFilterOption } from '../../shared/useFilterOption';
import { RangeValue } from './types';
import { shouldShowQuestionnaireTemplate } from '../../../components/StatisticsV2/OutgoingStatistics/scenarioMetadata';
import {
  getTaskCampaignGroups,
  retainTaskCampaignFilters,
} from '../../../models/Task/campaignFilters';
import type { TaskCampaignFilters } from '../../../models/Task/campaignFilters';
import {
  getTaskFormTemplates,
  selectTaskFormTemplates,
} from '../../../store/reducers/TaskFormSlice';
import { buildOutgoingStatisticRows } from '../../../components/StatisticsV2/OutgoingStatistics/buildOutgoingStatisticRows';

export const useOutgoingStatistics = () => {
  const {
    status: statisticsStatus,
    lastSuccessfulRequest,
    ...statistic
  } = useAppSelector((state) => state.outgoingStatistics);
  const clinics = useAppSelector(selectClinics);
  const templates = useAppSelector(selectTaskFormTemplates);
  const apiScenarioOptions = useAppSelector(selectOutboundTaskTypeOptions);

  const [statisticsRequest, setStatisticsRequest] =
    useState<OutgoingStatisticsRequest>({
      From: dayjs().format('YYYY-MM-DD'),
      To: dayjs().format('YYYY-MM-DD'),
      ClinicIds: [],
    });

  const dispatch = useAppDispatch();
  const [selectedClinics, setSelectedClinics] = useState<string[]>([]);

  const [selectedScenario, setSelectedScenario] = useState<string | null>(null);

  useEffect(() => {
    dispatch(getClinics());
    dispatch(getOutboundTaskTypes());
    dispatch(getTaskFormTemplates(undefined));
  }, [dispatch]);

  useEffect(() => {
    const request = dispatch(getOutgoingStatistics(statisticsRequest));

    return () => {
      request.abort();
    };
  }, [dispatch, statisticsRequest]);

  useEffect(() => {
    if (clinics.length !== 1 || selectedClinics.length > 0) {
      return;
    }

    const onlyClinicId = clinics[0].id;
    setSelectedClinics([onlyClinicId]);
    setStatisticsRequest((prev) => ({
      ...prev,
      ClinicIds: [onlyClinicId],
    }));
  }, [clinics, selectedClinics.length]);

  const scenarioOptions = apiScenarioOptions;

  useEffect(() => {
    if (scenarioOptions.length !== 1 || selectedScenario) {
      return;
    }

    const onlyScenarioValue = scenarioOptions[0].value;
    setSelectedScenario(onlyScenarioValue);
    setStatisticsRequest((prev) => ({
      ...prev,
      Types: [onlyScenarioValue],
    }));
  }, [scenarioOptions, selectedScenario]);

  const dataSource = useMemo(
    () => buildOutgoingStatisticRows(statistic, selectedScenario),
    [selectedScenario, statistic],
  );

  const dateRange = useMemo<[Dayjs, Dayjs]>(
    () => [dayjs(statisticsRequest.From), dayjs(statisticsRequest.To)],
    [statisticsRequest.From, statisticsRequest.To],
  );

  const onClinicsChange = (value: string[]) => {
    setSelectedClinics(value);
    if (Array.isArray(value) && value.length === 0) {
      handleClinics(value);
    }
  };

  const handleClinics = useCallback(
    (e: string[]) => {
      setStatisticsRequest((prev) => ({
        ...prev,
        ClinicIds: e,
      }));
    },
    [setStatisticsRequest, statisticsRequest],
  );

  const handleRangePicker = useCallback(
    (values: RangeValue<Dayjs>, formatString: string[]) => {
      if (!values || !formatString[0] || !formatString[1]) {
        return;
      }

      setStatisticsRequest((prev) => ({
        ...prev,
        From: formatDate(formatString[0]),
        To: formatDate(formatString[1]),
      }));
    },
    [setStatisticsRequest, statisticsRequest],
  );

  const filterOption = useFilterOption();

  const handleScenarioChange = useCallback(
    (value: string | undefined) => {
      const nextScenario = value ?? null;

      setSelectedScenario(nextScenario);
      const nextTypes = nextScenario ? [nextScenario] : [];
      setStatisticsRequest((prev) => {
        const {
          Types: _types,
          FormBatchIds: _formBatchIds,
          DispensarizationBatchIds: _dispensarizationBatchIds,
          FormTemplateIds: _formTemplateIds,
          ...rest
        } = prev;
        return {
          ...rest,
          ...(nextTypes.length ? { Types: nextTypes } : {}),
          ...retainTaskCampaignFilters(nextTypes, prev),
        };
      });
    },
    [setStatisticsRequest],
  );

  const onCampaignFiltersChange = useCallback((value: TaskCampaignFilters) => {
    setStatisticsRequest((prev) => {
      const {
        FormBatchIds: _formBatchIds,
        DispensarizationBatchIds: _dispensarizationBatchIds,
        ...rest
      } = prev;
      return {
        ...rest,
        ...retainTaskCampaignFilters(prev.Types ?? [], {
          ...value,
          FormTemplateIds: prev.FormTemplateIds,
        }),
      };
    });
  }, []);

  const onTemplatesChange = useCallback(
    (value: string[]) => {
      setStatisticsRequest((prev) => {
        const { FormTemplateIds: _templateIds, ...rest } = prev;

        const scenarioForRequest = prev.Types?.[0] ?? selectedScenario;

        if (
          !value.length ||
          !shouldShowQuestionnaireTemplate(scenarioForRequest)
        ) {
          return rest;
        }

        return {
          ...rest,
          FormTemplateIds: value,
        };
      });
    },
    [selectedScenario],
  );

  const handleOpenClinics = useCallback(
    (open: boolean) => {
      if (!open) {
        handleClinics(selectedClinics);
      }
    },
    [selectedClinics, handleClinics],
  );

  const campaignGroups = getTaskCampaignGroups(statisticsRequest.Types ?? []);
  const showCampaignFilter =
    campaignGroups.form || campaignGroups.dispensarization;
  const showTemplateFilter = shouldShowQuestionnaireTemplate(selectedScenario);
  const handleOpenTemplates = useCallback((_open: boolean) => {}, []);
  const campaignFilters = useMemo(
    () => ({
      FormBatchIds: statisticsRequest.FormBatchIds,
      DispensarizationBatchIds: statisticsRequest.DispensarizationBatchIds,
    }),
    [
      statisticsRequest.FormBatchIds,
      statisticsRequest.DispensarizationBatchIds,
    ],
  );

  const canExportStatistics =
    !statisticsStatus.responseLoading &&
    !statisticsStatus.responseError &&
    lastSuccessfulRequest !== undefined &&
    JSON.stringify(lastSuccessfulRequest) === JSON.stringify(statisticsRequest);

  return {
    dataSource,
    clinics,
    templates,
    scenarioOptions,
    handleClinics,
    handleRangePicker,
    dateRange,
    selectedClinics,
    selectedScenario,
    campaignFilters,
    selectedTemplates: statisticsRequest.FormTemplateIds ?? [],
    onClinicsChange,
    handleScenarioChange,
    onCampaignFiltersChange,
    onTemplatesChange,
    filterOption,
    handleOpenClinics,
    handleOpenTemplates,
    showCampaignFilter,
    showTemplateFilter,
    statisticsRequest,
    isLoading: statisticsStatus.responseLoading,
    canExportStatistics,
  };
};
